/***************************************************************************************
 * FileName      : ScenarioService.ts
 * Description   : 시나리오 JSON 파일 로드 및 관리 서비스
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.11
 * Modified Date : 2026.02.13 6개 시나리오 JSON 파일 메타데이터 등록 (열교환기 3개, 증류 1개, 순환 2개)
 * Reference     : packages/scjeil-smart_ict_lab-app/assets/json/scenario/*.json
 ***************************************************************************************/

// 2026.02.11 : 박은정 : 시나리오 타입 임포트
import { ScenarioFile } from '../types/scenario';

// 2026.02.11 : 박은정 : 시나리오 JSON 파일은 런타임에 fetch로 로드 (TypeScript 컴파일 오류 방지)
// 2026.02.11 : 박은정 : 시나리오 캐시
const scenarioCache: Record<string, ScenarioFile> = {};

// 2026.02.11 : 박은정 : 시나리오 메타데이터 (목록 조회용)
// 2026.02.13 : 박은정 : 6개 시나리오 JSON 파일 기반으로 메타데이터 등록 (장비별 시뮬레이션 목록 조회용)
const scenarioMetadata: Record<string, { equipmentId: string; name: string; duration: number }> = {
  'heat-exchanger-pp102-manual': {
    equipmentId: 'heat-exchanger',
    name: '열교환설비 PP-102 수동 운전',
    duration: 26,
  },
  'heat-exchanger-hotline': {
    equipmentId: 'heat-exchanger',
    name: '열교환기 히터부 성능 알아보기',
    duration: 18,
  },
  'heat-exchanger-coldline': {
    equipmentId: 'heat-exchanger',
    name: '열교환기 칠러부 성능 알아보기',
    duration: 16,
  },
  'distillation-auto': {
    equipmentId: 'distillation',
    name: '증류설비 정상운전 시나리오',
    duration: 6,
  },
  'circulation-pp302-manual': {
    equipmentId: 'circulation',
    name: '순환설비 PP-302 수동운전 응답 시험',
    duration: 28,
  },
  'circulation-bf304-manual': {
    equipmentId: 'circulation',
    name: '순환설비 BF-304 수동운전 응답 시험',
    duration: 24,
  },
};

// 2026.02.11 : 박은정 : 시나리오 서비스 클래스 (싱글톤)
export class ScenarioService {
  private static instance: ScenarioService | null = null;

  private constructor() {}

  // 2026.02.11 : 박은정 : 싱글톤 인스턴스 반환
  public static getInstance(): ScenarioService {
    if (!ScenarioService.instance) {
      ScenarioService.instance = new ScenarioService();
    }
    return ScenarioService.instance;
  }

  // 2026.02.11 : 박은정 : 시나리오 로드
  public async loadScenario(scenarioId: string): Promise<ScenarioFile> {
    // 2026.02.11 : 박은정 : 캐시 확인
    if (scenarioCache[scenarioId]) {
      return scenarioCache[scenarioId];
    }
    
    // 2026.02.11 : 박은정 : 런타임에 fetch로 로드
    try {
      const response = await fetch(`./assets/json/scenario/${scenarioId}.json`);
      if (!response.ok) {
        throw new Error(`시나리오를 찾을 수 없습니다: ${scenarioId}`);
      }
      const scenario = await response.json() as ScenarioFile;
      scenarioCache[scenarioId] = scenario;
      return scenario;
    } catch (error) {
      throw new Error(`시나리오 로드 실패: ${scenarioId} - ${error}`);
    }
  }

  // 2026.02.11 : 박은정 : 장비별 시나리오 목록 조회
  public getScenarioList(equipmentId: string): Array<{ id: string; name: string; duration: number }> {
    const scenarios: Array<{ id: string; name: string; duration: number }> = [];
    
    for (const [scenarioId, metadata] of Object.entries(scenarioMetadata)) {
      if (metadata.equipmentId === equipmentId) {
        scenarios.push({
          id: scenarioId,
          name: metadata.name,
          duration: metadata.duration,
        });
      }
    }
    
    return scenarios;
  }

  // 2026.02.11 : 박은정 : 모든 시나리오 목록 조회
  public getAllScenarios(): Array<{ id: string; name: string; equipmentId: string; duration: number }> {
    return Object.entries(scenarioMetadata).map(([scenarioId, metadata]) => ({
      id: scenarioId,
      name: metadata.name,
      equipmentId: metadata.equipmentId,
      duration: metadata.duration,
    }));
  }
}
